import React, { useMemo, useState } from 'react';
import { useFitStore } from '../../store/useFitStore';
import type { BillingInterval, CoachingPlan } from '../../store/types';
import { planPriceLabel, inclusionsSummary } from '../../lib/coachingPlans';

/* ============================================================
   PlansManager — the coach's reusable pricing plans ("$500/mo for
   3× 1-hour sessions/week"), created once and enrolled per client
   from the Client Hub. Money, not training content (that's the
   Library). Opened as a modal from Profile → Billing.
============================================================ */

const INTERVALS: { key: BillingInterval; label: string }[] = [
  { key: 'month', label: 'Monthly' },
  { key: 'week', label: 'Weekly' },
  { key: 'session', label: 'Per session' },
  { key: 'year', label: 'Yearly' },
];

// "500" / "49.99" → integer cents; null when unparseable. Never floats in state.
const dollarsToCents = (raw: string): number | null => {
  const n = parseFloat(raw.replace(/[$,\s]/g, ''));
  if (isNaN(n) || n < 0) return null;
  return Math.round(n * 100);
};
const centsToDollars = (cents?: number): string => {
  if (cents == null) return '';
  const d = cents / 100;
  return Number.isInteger(d) ? String(d) : d.toFixed(2);
};
const numOrUndef = (raw: string): number | undefined => {
  const n = parseInt(raw, 10);
  return isNaN(n) || n <= 0 ? undefined : n;
};

interface Props {
  onClose: () => void;
}

const PlansManager: React.FC<Props> = ({ onClose }) => {
  const { coachingPlans } = useFitStore();
  const [editing, setEditing] = useState<CoachingPlan | 'new' | null>(null);

  // Offered plans first, then hidden; newest first within each group.
  const sorted = useMemo(
    () =>
      [...coachingPlans].sort((a, b) =>
        a.isActive === b.isActive ? b.createdAt.localeCompare(a.createdAt) : a.isActive ? -1 : 1
      ),
    [coachingPlans]
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="bg-fit-bg-secondary rounded-fit-lg border border-fit-line w-full max-w-[560px] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-fit-line flex items-center justify-between gap-3">
          <div>
            <h2 className="text-[17px] font-bold tracking-tight">Pricing plans</h2>
            <p className="text-[12px] text-fit-text-secondary">Define a plan once, enroll clients from their profile.</p>
          </div>
          <button onClick={onClose} className="text-fit-text-secondary hover:text-white text-xl leading-none px-1" aria-label="Close">×</button>
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar p-5">
          {editing ? (
            <PlanEditor
              plan={editing === 'new' ? null : editing}
              onDone={() => setEditing(null)}
            />
          ) : (
            <>
              <button onClick={() => setEditing('new')} className="btn btn-primary btn-sm w-full mb-4">
                ＋ New plan
              </button>
              {sorted.length === 0 ? (
                <p className="text-[12px] text-fit-muted text-center py-8">
                  No plans yet — create your first, e.g. “Performance · $500/mo”.
                </p>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {sorted.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setEditing(p)}
                      className="rounded-fit-md border border-fit-line p-3.5 text-left hover:border-fit-line-strong transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[14px] font-bold truncate">
                          {p.name}
                          {!p.isActive && <span className="text-fit-muted font-normal text-[11px]"> · hidden</span>}
                        </p>
                        <p className="text-[14px] font-semibold tabular-nums shrink-0">{planPriceLabel(p)}</p>
                      </div>
                      {(inclusionsSummary(p.inclusions) || p.description) && (
                        <p className="text-[12px] text-fit-text-secondary mt-0.5 truncate">
                          {inclusionsSummary(p.inclusions) || p.description}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------------- Plan editor (create / edit / delete) ---------------- */

const PlanEditor: React.FC<{ plan: CoachingPlan | null; onDone: () => void }> = ({ plan, onDone }) => {
  const { user, upsertCoachingPlan, deleteCoachingPlan, showToast } = useFitStore();
  const [name, setName] = useState(plan?.name || '');
  const [price, setPrice] = useState(centsToDollars(plan?.priceCents));
  const [interval, setInterval_] = useState<BillingInterval>(plan?.billingInterval || 'month');
  const [description, setDescription] = useState(plan?.description || '');
  const [perWeek, setPerWeek] = useState(plan?.inclusions?.sessionsPerWeek ? String(plan.inclusions.sessionsPerWeek) : '');
  const [lengthMin, setLengthMin] = useState(plan?.inclusions?.sessionLengthMin ? String(plan.inclusions.sessionLengthMin) : '');
  const [total, setTotal] = useState(plan?.inclusions?.sessionsTotal ? String(plan.inclusions.sessionsTotal) : '');
  const [isActive, setIsActive] = useState(plan?.isActive ?? true);
  const [confirmDel, setConfirmDel] = useState(false);

  const cents = dollarsToCents(price);
  const valid = name.trim().length > 0 && cents != null;

  const save = () => {
    if (!valid || !user) return;
    const inclusions = {
      sessionsPerWeek: numOrUndef(perWeek),
      sessionLengthMin: numOrUndef(lengthMin),
      sessionsTotal: numOrUndef(total),
    };
    upsertCoachingPlan({
      id: plan?.id || crypto.randomUUID(),
      coachId: plan?.coachId || user.id,
      name: name.trim(),
      priceCents: cents!,
      billingInterval: interval,
      currency: plan?.currency || 'usd',
      description: description.trim() || undefined,
      inclusions,
      isActive,
      createdAt: plan?.createdAt || new Date().toISOString(),
    });
    showToast(plan ? 'Plan updated' : 'Plan created');
    onDone();
  };

  const remove = () => {
    if (!plan) return;
    deleteCoachingPlan(plan.id);
    showToast('Plan deleted — enrolled clients keep their saved rate');
    onDone();
  };

  return (
    <div>
      <div className="mb-4">
        <p className="mono-label mb-1.5">Plan name</p>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Performance" className="input" />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <p className="mono-label mb-1.5">Price (USD)</p>
          <input value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" placeholder="500" className="input" />
        </div>
        <div>
          <p className="mono-label mb-1.5">Billed</p>
          <div className="flex items-center gap-1 flex-wrap">
            {INTERVALS.map((i) => (
              <button
                key={i.key}
                onClick={() => setInterval_(i.key)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                  interval === i.key ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
                }`}
              >
                {i.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <p className="mono-label mb-1.5">What's included</p>
        <div className="grid grid-cols-3 gap-2">
          <input value={perWeek} onChange={(e) => setPerWeek(e.target.value)} inputMode="numeric" placeholder="Sessions/wk" className="input" />
          <input value={lengthMin} onChange={(e) => setLengthMin(e.target.value)} inputMode="numeric" placeholder="Min each" className="input" />
          <input value={total} onChange={(e) => setTotal(e.target.value)} inputMode="numeric" placeholder="Total (opt.)" className="input" />
        </div>
      </div>

      <div className="mb-4">
        <p className="mono-label mb-1.5">Description</p>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Full programming, nutrition, and weekly check-ins."
          className="input"
          style={{ minHeight: 64, resize: 'none' }}
        />
      </div>

      <div className="mb-5">
        <p className="mono-label mb-1.5">Visibility</p>
        <div className="flex items-center gap-1">
          {([true, false] as const).map((v) => (
            <button
              key={String(v)}
              onClick={() => setIsActive(v)}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em] transition-colors ${
                isActive === v ? 'bg-white text-fit-ink' : 'bg-fit-bg-tertiary text-fit-text-secondary hover:text-white'
              }`}
            >
              {v ? 'Offered' : 'Hidden'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={save} disabled={!valid} className="btn btn-primary btn-sm flex-1 disabled:opacity-40">
          {plan ? 'Save plan' : 'Create plan'}
        </button>
        <button onClick={onDone} className="btn btn-glass btn-sm">Cancel</button>
      </div>

      {plan && (
        <div className="mt-4 pt-4 border-t border-fit-line">
          {confirmDel ? (
            <div className="flex items-center gap-2.5">
              <span className="text-[12px] text-fit-text-secondary">Delete this plan? Enrolled clients keep their saved rate.</span>
              <button onClick={remove} className="text-[12px] font-semibold text-white underline underline-offset-2">Delete</button>
              <button onClick={() => setConfirmDel(false)} className="text-[12px] text-fit-muted hover:text-white">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setConfirmDel(true)} className="text-[12px] text-fit-muted hover:text-white underline underline-offset-2">
              Delete plan
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PlansManager;
